import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { usePortalSubscriptionPlans } from '@/modules/portal/hooks/usePortalSubscriptionPlans';
import { fetchPortalPricingCatalog } from '@/modules/portal/services/portalPricingCatalog.api';
import type {
  PortalPricingPlanCode,
  PortalPricingPlanPrice,
} from '@/modules/portal/types/portalPricingCatalog';
import { PortalLoadingState } from '@/modules/portal/components/ui/portal-loading';
import {
  PortalFadeIn,
  PortalStagger,
  PortalStaggerItem,
} from '@/modules/portal/components/ui/portal-motion';
import { BillingPeriodToggle } from '@/modules/portal/components/subscription-plans/BillingPeriodToggle';
import { SubscriptionPlanCard } from '@/modules/portal/components/subscription-plans/SubscriptionPlanCard';
import { UpgradePreviewDrawer } from '@/modules/portal/components/subscription-plans/UpgradePreviewDrawer';
import type { PortalSubscriptionPlan } from '@/modules/portal/types/portalPlans';
import { Button } from '@/shared/components/ui/button';
import { Icons } from '@/shared/components/ui/icons';
import { cn } from '@/shared/lib/cn';

type SubscriptionPlansCarouselProps = {
  enabled?: boolean;
  className?: string;
};

export function SubscriptionPlansCarousel({
  enabled = true,
  className,
}: SubscriptionPlansCarouselProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language.startsWith('ar') ? 'ar-SA' : 'en-US';
  const scrollRef = useRef<HTMLDivElement>(null);
  const [upgradePreviewPlan, setUpgradePreviewPlan] = useState<PortalSubscriptionPlan | null>(null);
  const [displayCurrency, setDisplayCurrency] = useState('EGP');

  const pricingCatalogQuery = useQuery({
    queryKey: ['customer', 'subscription', 'pricing-catalog'],
    queryFn: fetchPortalPricingCatalog,
    enabled,
    staleTime: 5 * 60 * 1000,
  });

  const availableCurrencies =
    pricingCatalogQuery.data?.currencies.map((currency) => currency.code.trim().toUpperCase()) ??
    [];

  useEffect(() => {
    if (availableCurrencies.length === 0) return;

    if (!availableCurrencies.includes(displayCurrency)) {
      const fallbackCurrency = availableCurrencies.includes('EGP')
        ? 'EGP'
        : (availableCurrencies[0] ?? 'EGP');

      setDisplayCurrency(fallbackCurrency);
    }
  }, [availableCurrencies, displayCurrency]);

  const resolveDisplayPrice = useCallback(
    (plan: PortalSubscriptionPlan): PortalPricingPlanPrice | null => {
      const planCode = plan.plan_code as PortalPricingPlanCode;
      const pricingPlan = pricingCatalogQuery.data?.plans[planCode];

      if (!pricingPlan) return null;

      return (
        pricingPlan.prices.find(
          (price) =>
            price.currency.trim().toUpperCase() === displayCurrency &&
            price.billingCycle === plan.billing_cycle,
        ) ?? null
      );
    },
    [displayCurrency, pricingCatalogQuery.data],
  );

  const handleUpgradeClick = useCallback((plan: PortalSubscriptionPlan) => {
    setUpgradePreviewPlan(plan);
  }, []);

  const handleCloseUpgradePreview = useCallback(() => {
    setUpgradePreviewPlan(null);
  }, []);

  const {
    isLoading,
    error,
    refetch,
    billingPeriod,
    setBillingPeriod,
    visiblePlans,
    availableBillingPeriods,
  } = usePortalSubscriptionPlans(enabled);

  const scrollBy = useCallback((direction: 'prev' | 'next') => {
    const container = scrollRef.current;
    if (!container) return;
    const offset =
      direction === 'next' ? container.clientWidth * 0.85 : -container.clientWidth * 0.85;
    container.scrollBy({ left: offset, behavior: 'smooth' });
  }, []);

  if (!enabled) {
    return null;
  }

  if (isLoading || pricingCatalogQuery.isLoading) {
    return (
      <section className={cn('space-y-4', className)}>
        <div>
          <h2 className="text-base font-semibold text-foreground">{t('portal.plans.title')}</h2>
          <p className="mt-1 text-sm text-muted-foreground">{t('portal.plans.description')}</p>
        </div>
        <PortalLoadingState
          size="md"
          message={t('portal.plans.loading')}
          className="min-h-[240px] justify-center"
        />
      </section>
    );
  }

  if (error || pricingCatalogQuery.error) {
    return (
      <section className={cn('space-y-4', className)}>
        <div>
          <h2 className="text-base font-semibold text-foreground">{t('portal.plans.title')}</h2>
          <p className="mt-1 text-sm text-muted-foreground">{t('portal.plans.description')}</p>
        </div>
        <div className="portal-glass-panel mx-auto max-w-lg space-y-4 rounded-2xl border-destructive/30 p-6">
          <h3 className="text-lg font-semibold text-foreground">{t('portal.plans.error.title')}</h3>
          <p className="text-sm text-muted-foreground">{t('portal.plans.error.message')}</p>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              void refetch();
              void pricingCatalogQuery.refetch();
            }}
          >
            {t('common.retry')}
          </Button>
        </div>
      </section>
    );
  }

  if (!billingPeriod) {
    return null;
  }

  return (
    <PortalFadeIn className={cn('space-y-4', className)}>
      <div className="mx-auto max-w-3xl text-center">
        <h2 className="text-2xl font-bold text-foreground">اختر الخطة المناسبة لنمو أعمالك</h2>
        <p className="mt-2 text-sm leading-6 text-muted-foreground">
          قارن بين الخطط ودورات الفوترة واختر الأنسب لاحتياجات شركتك.
        </p>

        <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
          <div className="rounded-2xl border border-border/60 bg-background/80 p-1 shadow-sm">
            <select
              value={displayCurrency}
              onChange={(event) => setDisplayCurrency(event.target.value)}
              className="h-10 rounded-xl bg-transparent px-4 text-sm font-semibold text-foreground outline-none"
              aria-label="عملة العرض"
            >
              {availableCurrencies.map((currency) => (
                <option key={currency} value={currency}>
                  {currency}
                </option>
              ))}
            </select>
          </div>

          <BillingPeriodToggle
            value={billingPeriod}
            periods={availableBillingPeriods}
            onChange={setBillingPeriod}
          />
        </div>
      </div>

      {visiblePlans.length === 0 ? (
        <div className="portal-empty-state portal-glass-panel">
          <Icons.Layers className="mx-auto h-10 w-10 text-muted-foreground/50" aria-hidden />
          <p className="mt-4 text-lg font-semibold text-foreground">
            {t('portal.plans.empty.title')}
          </p>
          <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
            {t('portal.plans.empty.message')}
          </p>
        </div>
      ) : (
        <div className="relative">
          {visiblePlans.length > 1 ? (
            <div className="mb-3 hidden justify-end gap-2 sm:flex">
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="rounded-xl"
                aria-label={t('portal.plans.carousel.previous')}
                onClick={() => scrollBy('prev')}
              >
                <Icons.ChevronRight className="h-4 w-4 rotate-180" />
              </Button>
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="rounded-xl"
                aria-label={t('portal.plans.carousel.next')}
                onClick={() => scrollBy('next')}
              >
                <Icons.ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          ) : null}

          <div
            ref={scrollRef}
            className="mx-auto flex max-w-7xl snap-x snap-mandatory justify-center gap-6 overflow-x-auto px-4 pb-4 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            <PortalStagger className="mx-auto flex justify-center gap-6">
              {visiblePlans.map((plan) => (
                <PortalStaggerItem key={plan.id} className="snap-start">
                  <SubscriptionPlanCard
                    plan={plan}
                    locale={locale}
                    onUpgradeClick={handleUpgradeClick}
                    displayPrice={resolveDisplayPrice(plan)}
                  />
                </PortalStaggerItem>
              ))}
            </PortalStagger>
          </div>
        </div>
      )}

      <p className="text-center text-xs text-muted-foreground">{t('portal.plans.safeNote')}</p>

      {upgradePreviewPlan && billingPeriod ? (
        <UpgradePreviewDrawer
          plan={upgradePreviewPlan}
          billingCycle={billingPeriod}
          onClose={handleCloseUpgradePreview}
        />
      ) : null}
    </PortalFadeIn>
  );
}
